import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Flex, Spinner } from "@radix-ui/themes";
import { RadioCardGroup } from "@/components/shared/inputs/radio-group/RadioCardGroup";
import { ColorPicker } from "@/components/shared/color-picker/ColorPicker";
import {
  ChatSettings,
  QuackChatConfigResponse,
  SvgRadius,
} from "@/components/quack-chat/QuackChatConfig.model";
import Select, { SelectOption } from "@/components/shared/select";
import { defaultTheme, QuackChat } from "@/components/quack-chat/QuackChat";
import { useQuackchatPlatformConfiguration } from "@/api/quackchat/configuration/useQuackchatPlatformConfiguration";
import { useUploadQuackchatAssest } from "@/api/quackchat/configuration/useUploadQuackchatAssest";
import { CollapsibleFieldList } from "../parts/CollapsibleFieldList";
import { FieldGroup, OptionalFieldGroup } from "../parts/FieldGroup";
import { FileUploadComponent } from "../parts/FileUploadComponent";
import { Message } from "@/components/quack-chat/core/entities/Message";
import { AlignEndVertical, AlignStartVertical, ChevronDown, Minus } from "lucide-react";
import { AutomationConfigurationContentHeader } from "../parts/AutomationConfigurationContentHeader";
import styles from "../AutomationConfiguration.module.scss";

const SYSTEM_DEFAULT_FONT = "system-default";

const fontFamilyOptions: SelectOption[] = [
  { label: "System Default", value: SYSTEM_DEFAULT_FONT },
  { label: "Inter", value: "Inter, sans-serif" },
  { label: "Geist", value: "Geist, sans-serif" },
  { label: "Roboto", value: "Roboto, sans-serif" },
  { label: "Open Sans", value: "'Open Sans', sans-serif" },
  { label: "Lato", value: "Lato, sans-serif" },
  { label: "Poppins", value: "Poppins, sans-serif" },
  { label: "Nunito", value: "Nunito, sans-serif" },
  { label: "Montserrat", value: "Montserrat, sans-serif" },
];

const iconSizeOptions: SelectOption[] = [
  { label: "1", hint: "24px", value: "24" },
  { label: "2", hint: "32px", value: "32" },
  { label: "3", hint: "40px", value: "40" },
  { label: "4", hint: "48px", value: "48" },
  { label: "5", hint: "56px", value: "56" },
];

function isValidSvgRadius(value: string): value is SvgRadius {
  return value === "small" || value === "medium" || value === "large" || value === "full";
}

const svgRadiusOptions: SelectOption[] = [
  { label: "Small", value: "small" },
  { label: "Medium", value: "medium" },
  { label: "Large", value: "large" },
  { label: "Full", value: "full" },
];

export function QuackchatChannelAppearanceConfiguration({
  simulatorId,
}: {
  simulatorId: string | undefined;
}) {
  const {
    data: configuration,
    isLoading,
    updateStatus,
    update: updateConfiguration,
  } = useQuackchatPlatformConfiguration(simulatorId);

  const { mutateAsync: uploadAssest } = useUploadQuackchatAssest(simulatorId);

  const configurationRef = useRef(configuration);

  const [errors, setErrors] = useState<Record<string, string>>({});
  const messages = useMemo<Message[]>(() => {
    return [
      {
        action: "standard_message",
        author: "USER",
        text: "Why aren't my reviews displaying on the website?",
        deduplicationKey: "example_message_1",
        createdAt: "2021-09-01T12:00:00Z",
        data: {},
      },
      {
        action: "standard_message",
        author: "AUTO_AGENT",
        text: "This issue requires further assistance. I'm forwarding your request to a live agent, who can help resolve it. Please hold on while I transfer you.",
        deduplicationKey: "example_message_2",
        createdAt: "2021-09-01T12:10:00Z",
        data: {},
      },
      {
        action: "standard_message",
        author: "AUTO_AGENT",
        text: "You are now connected to John Doe. They will assist you further.",
        deduplicationKey: "example_message_3",
        createdAt: "2021-09-01T12:20:00Z",
        data: {},
      },
      {
        action: "standard_message",
        author: "LIVE_AGENT",
        text: "Hi, I'm John, I'm here to help you.",
        deduplicationKey: "example_message_4",
        createdAt: "2021-09-01T12:30:00Z",
        data: {
          agentDisplayName: "John Doe",
        },
      },
    ];
  }, []);

  const updateChatSettings = useCallback(
    (chatSettings: Partial<ChatSettings>) => {
      if (configurationRef.current == null) {
        return;
      }

      updateConfiguration({
        ...configurationRef.current,
        chatSettings: {
          ...configurationRef.current.chatSettings,
          ...chatSettings,
        },
      });
    },
    [updateConfiguration],
  );

  const removeFromChatSettings = useCallback(
    (key: keyof ChatSettings) => {
      if (configurationRef.current == null) {
        return;
      }

      const newConfiguration: QuackChatConfigResponse = {
        ...configurationRef.current,
      };

      delete newConfiguration.chatSettings[key];
      updateConfiguration(newConfiguration);
    },
    [updateConfiguration],
  );

  const updateTheme = useCallback(
    (theme: Partial<ChatSettings["theme"]>) => {
      if (configurationRef.current == null) {
        return;
      }

      updateChatSettings({
        theme: {
          ...defaultTheme,
          ...configurationRef.current.chatSettings.theme,
          ...theme,
        },
      });
    },
    [updateChatSettings],
  );

  const handleUploadAssest = useCallback(
    async (file: File) => {
      return uploadAssest(file);
    },
    [uploadAssest],
  );

  useEffect(() => {
    configurationRef.current = configuration;
  }, [configuration]);

  if (isLoading || configuration == null) {
    return <Spinner />;
  }

  const { chatSettings } = configuration;

  return (
    <Flex gap={"24px"}>
      <Flex flexGrow={"1"} direction={"column"} gap={"24px"}>
        <AutomationConfigurationContentHeader title={"Appearance"} status={updateStatus} />

        <Flex direction={"column"}>
          <CollapsibleFieldList title={"Theme"} defaultOpen>
            <FieldGroup horizontal description={"AI Avatar"} error={errors?.avatarSVG}>
              <FileUploadComponent
                defaultFileName={chatSettings.avatarSVG?.split("/").pop()}
                onFileUpload={handleUploadAssest}
                onSuccess={(filePath) => {
                  if (filePath === "") {
                    removeFromChatSettings("avatarSVG");
                  } else {
                    updateChatSettings({
                      avatarSVG: filePath === "" ? undefined : filePath,
                    });
                  }
                }}
                onError={(error) => {
                  setErrors((prev) => ({
                    ...prev,
                    avatarSVG: error,
                  }));
                }}
              />
            </FieldGroup>

            <FieldGroup horizontal description={"User Avatar"} error={errors?.userAvatarSVG}>
              <FileUploadComponent
                defaultFileName={chatSettings.userAvatarSVG?.split("/").pop()}
                onFileUpload={handleUploadAssest}
                onSuccess={(filePath) => {
                  if (filePath === "") {
                    removeFromChatSettings("userAvatarSVG");
                  } else {
                    updateChatSettings({
                      userAvatarSVG: filePath === "" ? undefined : filePath,
                    });
                  }
                }}
                onError={(error) => {
                  setErrors((prev) => ({
                    ...prev,
                    userAvatarSVG: error,
                  }));
                }}
              />
            </FieldGroup>
          </CollapsibleFieldList>

          <CollapsibleFieldList title={"Widget"} defaultOpen>
            <FieldGroup horizontal description={"Widget Logo"} error={errors?.customSVG}>
              <FileUploadComponent
                defaultFileName={chatSettings.customSVG?.split("/").pop()}
                onFileUpload={handleUploadAssest}
                onSuccess={(filePath) => {
                  if (filePath === "") {
                    removeFromChatSettings("customSVG");
                  } else {
                    updateChatSettings({
                      customSVG: filePath,
                    });
                  }
                }}
                onError={(error) => {
                  setErrors((prev) => ({
                    ...prev,
                    customSVG: error,
                  }));
                }}
              />
            </FieldGroup>

            <FieldGroup horizontal description={"Widget Logo when opened"} error={errors?.closeSVG}>
              <FileUploadComponent
                defaultFileName={chatSettings.closeSVG?.split("/").pop()}
                onFileUpload={handleUploadAssest}
                onSuccess={(filePath) => {
                  if (filePath === "") {
                    removeFromChatSettings("closeSVG");
                  } else {
                    updateChatSettings({
                      closeSVG: filePath,
                    });
                  }
                }}
                onError={(error) => {
                  setErrors((prev) => ({
                    ...prev,
                    closeSVG: error,
                  }));
                }}
              />
            </FieldGroup>

            <FieldGroup horizontal description={"Widget position alignemnt"}>
              <RadioCardGroup
                value={chatSettings.position ?? "bottom-right"}
                onChange={(newValue) => {
                  if (newValue === "bottom-left" || newValue === "bottom-right") {
                    updateChatSettings({
                      position: newValue,
                    });
                  }
                }}
                options={[
                  {
                    value: "bottom-right",
                    label: "Right",
                    icon: <AlignEndVertical size={"16"} />,
                  },
                  {
                    value: "bottom-left",
                    label: "Left",
                    icon: <AlignStartVertical size={"16"} />,
                  },
                ]}
              />
            </FieldGroup>

            <FieldGroup horizontal description={"Minimize icon"}>
              <RadioCardGroup
                value={chatSettings.minimizeIcon ?? "chevron"}
                onChange={(newValue) => {
                  if (newValue === "chevron" || newValue === "dash") {
                    updateChatSettings({
                      minimizeIcon: newValue,
                    });
                  }
                }}
                options={[
                  {
                    value: "chevron",
                    label: "Chevron",
                    icon: <ChevronDown size={"16"} />,
                  },
                  {
                    value: "dash",
                    label: "Line",
                    icon: <Minus size={"16"} />,
                  },
                ]}
              />
            </FieldGroup>

            <FieldGroup horizontal description={"Widget size"}>
              <Select
                value={chatSettings.iconSize?.toString()}
                onChange={(newValue) => {
                  updateChatSettings({
                    iconSize: parseInt(newValue),
                  });
                }}
                options={iconSizeOptions}
                classes={{
                  trigger: styles.Select,
                  value: styles.SelectValue,
                }}
              />
            </FieldGroup>

            <FieldGroup horizontal description={"Svgs Radius"}>
              <Select
                value={chatSettings.svgRadius}
                onChange={(newValue) => {
                  updateChatSettings({
                    svgRadius: isValidSvgRadius(newValue) ? newValue : undefined,
                  });
                }}
                options={svgRadiusOptions}
                classes={{
                  trigger: styles.Select,
                  value: styles.SelectValue,
                }}
              />
            </FieldGroup>

            <FieldGroup horizontal description={"Font Family"}>
              <Select
                value={chatSettings.theme?.fontFamily ?? SYSTEM_DEFAULT_FONT}
                onChange={(newValue) => {
                  updateTheme({
                    fontFamily: newValue === SYSTEM_DEFAULT_FONT ? undefined : newValue,
                  });
                }}
                options={fontFamilyOptions}
                classes={{
                  trigger: styles.Select,
                  value: styles.SelectValue,
                }}
              />
            </FieldGroup>
          </CollapsibleFieldList>

          <CollapsibleFieldList title={"Customize Color Theme"} defaultOpen>
            <FieldGroup horizontal description={"Agent Text"}>
              <ColorPicker
                value={chatSettings.theme?.agentText}
                onChange={(newValue) => {
                  updateTheme({
                    agentText: newValue,
                  });
                }}
              />
            </FieldGroup>

            <FieldGroup horizontal description={"Agent Background"}>
              <ColorPicker
                value={chatSettings.theme?.agentBackground}
                onChange={(newValue) => {
                  updateTheme({
                    agentBackground: newValue,
                  });
                }}
              />
            </FieldGroup>

            <FieldGroup horizontal description={"User Text"}>
              <ColorPicker
                value={chatSettings.theme?.userText}
                onChange={(newValue) => {
                  updateTheme({
                    userText: newValue,
                  });
                }}
              />
            </FieldGroup>

            <OptionalFieldGroup
              horizontal
              description={"User Background"}
              enabled={chatSettings?.showUserBackground === true}
              toggleEnabled={(nextValue) => {
                updateChatSettings({ showUserBackground: nextValue });
              }}
            >
              <ColorPicker
                value={chatSettings.theme?.userBackground}
                onChange={(newValue) => {
                  updateTheme({
                    userBackground: newValue,
                  });
                }}
                disabled={chatSettings?.showUserBackground === false}
              />
            </OptionalFieldGroup>

            <FieldGroup horizontal description={"Send Button Text"}>
              <ColorPicker
                value={chatSettings.theme?.textSendButton}
                onChange={(newValue) => {
                  updateTheme({
                    textSendButton: newValue,
                  });
                }}
              />
            </FieldGroup>

            <FieldGroup horizontal description={"Send Button Background"}>
              <ColorPicker
                value={chatSettings.theme?.bgSendButton}
                onChange={(newValue) => {
                  updateTheme({
                    bgSendButton: newValue,
                  });
                }}
              />
            </FieldGroup>

            <FieldGroup horizontal description={"Secondary Button Text"}>
              <ColorPicker
                value={chatSettings.theme?.textSecondaryButton}
                onChange={(newValue) => {
                  updateTheme({
                    textSecondaryButton: newValue,
                  });
                }}
              />
            </FieldGroup>

            <FieldGroup horizontal description={"Secondary Button Background"}>
              <ColorPicker
                value={chatSettings.theme?.bgSecondaryButton}
                onChange={(newValue) => {
                  updateTheme({
                    bgSecondaryButton: newValue,
                  });
                }}
              />
            </FieldGroup>

            <FieldGroup horizontal description={"Secondary Button Border"}>
              <ColorPicker
                value={chatSettings.theme?.borderSecondaryButton}
                onChange={(newValue) => {
                  updateTheme({
                    borderSecondaryButton: newValue,
                  });
                }}
              />
            </FieldGroup>

            <FieldGroup horizontal description={"Notification Badge"}>
              <ColorPicker
                value={chatSettings.theme?.bgNotificationBadge}
                onChange={(newValue) => {
                  updateTheme({
                    bgNotificationBadge: newValue,
                  });
                }}
              />
            </FieldGroup>
          </CollapsibleFieldList>
        </Flex>
      </Flex>

      <Flex flexShrink={"0"}>
        <QuackChat config={configuration} agents={["John Doe"]} messages={messages} />
      </Flex>
    </Flex>
  );
}
